import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface BillingInvoice {
  id: string;
  invoice_number: string;
  status: string;
  subtotal: number;
  tax_amount: number;
  total: number;
  total_receivable: number;
  amount_paid: number;
  outstanding_balance: number;
  issue_date: string;
  due_date: string;
  created_at: string;
}

export interface BillingPayment {
  id: string;
  amount: number;
  payment_method: string;
  payment_date: string;
  reference_number: string | null;
  invoice_number: string;
}

export interface BillingQuote {
  id: string;
  quote_number: string;
  status: string;
  subtotal: number;
  tax_amount: number;
  total: number;
  valid_until: string;
  accepted_at: string | null;
  created_at: string;
}

export interface BillingCreditNote {
  id: string;
  credit_note_number: string;
  amount: number;
  reason: string;
  status: string;
  created_at: string;
}

export interface BillingDebitNote {
  id: string;
  debit_note_number: string;
  amount: number;
  reason: string;
  status: string;
  issued_at: string | null;
  created_at: string;
}

export interface BillingWriteOff {
  id: string;
  write_off_number: string;
  amount: number;
  reason: string;
  status: string;
  transaction_date: string;
  invoice_number: string;
}

export interface BillingDiscount {
  id: string;
  discount_number: string;
  amount: number;
  discount_type: string;
  reason: string;
  status: string;
  transaction_date: string;
  invoice_number: string;
}

export interface BillingStats {
  totalInvoices: number;
  totalBilled: number;
  totalPaid: number;
  totalOutstanding: number;
}

export interface BillingSummaryResponse {
  invoices: BillingInvoice[];
  payments: BillingPayment[];
  quotes: BillingQuote[];
  creditNotes: BillingCreditNote[];
  debitNotes: BillingDebitNote[];
  writeOffs: BillingWriteOff[];
  discounts: BillingDiscount[];
  stats: BillingStats;
  pagination: { page: number; pageSize: number; total: number; totalPages: number };
}

export interface BillingActivityEvent {
  event_type: string;
  reference_id: string;
  reference_type: string;
  reference_number: string;
  amount: number;
  status: string;
  payment_method: string | null;
  event_date: string;
  description: string;
}

export interface BillingActivityResponse {
  activities: BillingActivityEvent[];
  pagination: { page: number; pageSize: number; total: number; totalPages: number };
}

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const contactBillingKeys = {
  all: (contactId: string) => ['contacts', contactId, 'billing'] as const,
  summary: (contactId: string, params?: { page?: number; status?: string }) =>
    [...contactBillingKeys.all(contactId), 'summary', params] as const,
  activity: (contactId: string, page?: number) =>
    [...contactBillingKeys.all(contactId), 'activity', page] as const,
};

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Fetch billing summary for a contact (invoices, payments, stats).
 */
export function useContactBillingSummary(
  contactId: string,
  params: { page?: number; pageSize?: number; status?: string } = {},
) {
  return useQuery({
    queryKey: contactBillingKeys.summary(contactId, params),
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params.page) searchParams.set('page', String(params.page));
      if (params.pageSize) searchParams.set('pageSize', String(params.pageSize));
      if (params.status) searchParams.set('status', params.status);

      const response = await apiClient.get<ApiResponse<BillingSummaryResponse>>(
        `/contacts/${contactId}/billing-summary?${searchParams.toString()}`,
      );
      return response.data.data;
    },
    enabled: !!contactId,
  });
}

/**
 * Fetch billing activity timeline for a contact.
 */
export function useContactBillingActivity(contactId: string, page = 1, pageSize = 20) {
  return useQuery({
    queryKey: contactBillingKeys.activity(contactId, page),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<BillingActivityResponse>>(
        `/contacts/${contactId}/billing-activity?page=${page}&pageSize=${pageSize}`,
      );
      return response.data.data;
    },
    enabled: !!contactId,
  });
}
